import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { CheckCircle, ArrowRight, Factory, Settings, Shield, Facebook } from "lucide-react";

const TankFabrication = () => {
  const materials = [
    { name: "Stainless Steel 304", grade: "SS304", applications: "Food & Beverage, General Storage" },
    { name: "Stainless Steel 316L", grade: "SS316L", applications: "Pharmaceuticals, Chemical Processing" },
    { name: "Duplex Steel", grade: "Duplex", applications: "High Corrosion Resistance" },
    { name: "Carbon Steel", grade: "CS", applications: "Industrial Storage, Water Treatment" },
    { name: "Titanium", grade: "Ti", applications: "Extreme Environments" },
    { name: "Hastelloy", grade: "HC", applications: "Chemical Reactors" }
  ];

  const fabricationOptions = [
    {
      icon: Factory,
      title: "Shop Fabrication",
      description: "Complete fabrication in our controlled facility",
      benefits: ["Quality control", "Weather protection", "Faster completion", "Better finishing"]
    },
    {
      icon: Settings,
      title: "Site Erection",
      description: "On-site assembly for large tanks and vessels",
      benefits: ["Large capacity tanks", "Reduced transport costs", "Custom installation", "Field engineering"]
    }
  ];

  const capabilities = [
    "Custom tank design and engineering",
    "Pressure vessel certification",
    "Dish-end forming and fabrication",
    "Advanced welding (TIG, MIG, Arc)",
    "Heat treatment services",
    "Hydrostatic testing",
    "Non-destructive testing (NDT)",
    "Complete piping integration"
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Custom Tank Fabrication | Stainless Steel Tanks Malaysia - WLE</title>
        <meta name="description" content="Custom stainless steel tank fabrication in Malaysia. SS304, SS316L, Duplex, Hastelloy. Shop fabrication or site erection with ASME, PED certification." />
        <link rel="canonical" href="https://wletec.com/services/tank-fabrication" />
        <meta property="og:title" content="Tank Fabrication Services - WLE Technology" />
        <meta property="og:description" content="Custom tank design and fabrication with complete EPCC solutions." />
        <meta property="og:url" content="https://wletec.com/services/tank-fabrication" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="tank fabrication, stainless steel tanks, SS316L, custom tanks, ASME, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="mb-4">Tank Fabrication</Badge>
            <h1 className="text-5xl font-bold text-foreground mb-6">
              Custom Tank <span className="text-wle-blue">Fabrication</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From concept to completion, we design and build custom stainless steel tanks and pressure vessels 
              with complete EPCC solutions for industries across Malaysia and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Fabrication Options */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Fabrication Options</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            {fabricationOptions.map((option, index) => (
              <Card key={index} className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-full bg-wle-blue/10">
                      <option.icon className="h-6 w-6 text-wle-blue" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{option.title}</CardTitle>
                      <CardDescription>{option.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {option.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Materials We Work With</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {materials.map((material, index) => (
              <Card key={index} className="hover:shadow-blue transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-lg">{material.name}</CardTitle>
                    <Badge variant="outline">{material.grade}</Badge>
                  </div>
                  <CardDescription>{material.applications}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-16">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Capabilities</h2>
              <p className="text-muted-foreground mb-8">
                With decades of experience and state-of-the-art equipment, we deliver tanks and vessels 
                that meet the most demanding specifications across various industries.
              </p>
              <ul className="space-y-4">
                {capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-wle-blue mt-0.5 flex-shrink-0" />
                    <span>{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <Card className="p-8">
                <div className="text-center">
                  <Shield className="h-16 w-16 text-wle-blue mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Quality Assured</h3>
                  <p className="text-muted-foreground mb-6">
                    All our tanks are built to international standards with proper certification and testing.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• ASME Code compliance</li>
                    <li>• PED certification available</li>
                    <li>• ISO 9001 quality system</li>
                    <li>• Material traceability</li>
                  </ul>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container">
          <Card className="bg-gradient-primary text-white">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h3>
              <p className="text-lg mb-8 opacity-90">
                Get a custom quote for your tank fabrication needs. Our engineers will work with you 
                from design to delivery.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" variant="secondary">
                  <Link to="/contact">
                    Get Quote Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
                  <a 
                    href="https://www.facebook.com/WLEtech" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    <Facebook className="h-4 w-4" />
                    Follow WLE
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TankFabrication;